import { createSelector } from "@reduxjs/toolkit";
import {
  DesignLeftSideBarTabs,
  type IDrofusInterfaceChangeType,
  type IDrofusInterfaceDiscipline,
} from "@multiconsult-group/multiplacer-core";
import { openapi, type PostMultiplacerDrofusInterfaceRoomChangesApiArg } from "../../openapi";
import { IRootState } from "../app/store";
import { selectUniqueMcbimSpaceNames } from "../McBim/extraMcBimSelectors";
import { DROFUS_CHANGE_TYPE_OPTIONS } from "../components/DrofusChangeTypeFilter";

const selectProjectNo = (state: IRootState) => state.design.projectNo;
const selectActiveLeftSideBarTab = (state: IRootState) => state.design.activeLeftSideBarTab;
const selectDrofusChangesDateFrom = (state: IRootState) => state.drofusChanges.dateFrom;
const selectDrofusChangesDateTo = (state: IRootState) => state.drofusChanges.dateTo;

export const selectDrofusChangesSelectedChangeTypes = (state: IRootState) =>
  state.drofusChanges.changeTypes;

export const selectDrofusChangesAffectedDisciplines = (state: IRootState) =>
  state.drofusChanges.affectedDisciplines;

export const selectDrofusChangeEventMatchesActiveFilters = createSelector(
  [selectDrofusChangesSelectedChangeTypes, selectDrofusChangesAffectedDisciplines],
  (selectedChangeTypes, affectedDisciplines) => {
    const selectedChangeTypeSet = new Set(selectedChangeTypes);
    const affectedDisciplineSet = new Set(affectedDisciplines);

    return (item: {
      changeType: string;
      affectedDisciplines?: string[];
    }): boolean => {
      if (
        selectedChangeTypeSet.size > 0 &&
        !selectedChangeTypeSet.has(item.changeType as IDrofusInterfaceChangeType)
      ) {
        return false;
      }
      if (affectedDisciplineSet.size === 0) return true;

      return (item.affectedDisciplines ?? []).some((discipline) =>
        affectedDisciplineSet.has(discipline as IDrofusInterfaceDiscipline)
      );
    };
  }
);

export const selectDrofusChangesPanelActive = createSelector(
  [selectActiveLeftSideBarTab],
  (activeTab) => activeTab === DesignLeftSideBarTabs.DROFUS_CHANGES
);

export const selectDrofusChangesRoomFunctionNumbersForActiveFloor = createSelector(
  [selectUniqueMcbimSpaceNames],
  (spaceNames): string[] => spaceNames.map((name) => name.trim()).filter(Boolean)
);

export const selectDrofusChangesApiArg = createSelector(
  [
    selectProjectNo,
    selectDrofusChangesDateFrom,
    selectDrofusChangesDateTo,
    selectDrofusChangesRoomFunctionNumbersForActiveFloor,
  ],
  (
    projectNo,
    dateFrom,
    dateTo,
    roomFunctionNumbers
  ): PostMultiplacerDrofusInterfaceRoomChangesApiArg | null => {
    if (!projectNo || !dateFrom || !dateTo || roomFunctionNumbers.length === 0) {
      return null;
    }

    return {
      body: {
        projectNo,
        roomFunctionNumbers,
        dateFrom,
        dateTo,
        limit: 500,
        offset: 0,
      },
    };
  }
);

export const selectDrofusChangesRoomColorByRoomNumber = createSelector(
  [
    selectDrofusChangesPanelActive,
    selectDrofusChangesApiArg,
    selectDrofusChangeEventMatchesActiveFilters,
    (state: IRootState) => state,
  ],
  (
    panelActive,
    apiArg,
    eventMatchesActiveFilters,
    state
  ): Record<string, { fill: string; stroke: string }> => {
    if (!panelActive || !apiArg) return {};

    const data =
      openapi.endpoints.postMultiplacerDrofusInterfaceRoomChanges.select(apiArg)(state).data;
    if (!data?.items.length) return {};

    const changeTypesByRoom = new Map<string, Set<IDrofusInterfaceChangeType>>();

    for (const item of data.items) {
      if (!eventMatchesActiveFilters(item)) continue;

      const roomNumber = item.context.room?.functionNo?.trim();
      if (!roomNumber) continue;

      const changeTypes = changeTypesByRoom.get(roomNumber) ?? new Set<IDrofusInterfaceChangeType>();
      changeTypes.add(item.changeType);
      changeTypesByRoom.set(roomNumber, changeTypes);
    }

    const colorsByRoom: Record<string, { fill: string; stroke: string }> = {};

    for (const [roomNumber, changeTypes] of changeTypesByRoom) {
      if (changeTypes.size > 1) {
        colorsByRoom[roomNumber] = {
          fill: "#1971c2",
          stroke: "#1971c2",
        };
        continue;
      }

      const [changeType] = Array.from(changeTypes);
      const option = DROFUS_CHANGE_TYPE_OPTIONS.find((item) => item.value === changeType);
      if (!option) continue;

      colorsByRoom[roomNumber] = {
        fill: option.activeBackground,
        stroke: option.activeBackground,
      };
    }

    return colorsByRoom;
  }
);
